<template>
  <div :class="[$style.teaser, { [$style.is24]: isIs24 }]">
    <div :class="[$style.content, { [$style.is24]: isIs24 }]">
      <h2>{{ $t(`landing.teaser.title`) }}</h2>
      <p>{{ $t(`landing.teaser.text`) }}</p>
    </div>
    <div :class="$style.images">
      <div v-if="!isIs24" data-testid="gradient-circle" :class="$style.gradientCircleContainer">
        <img
          :src="gradientCircleUrl"
          :class="$style.gradientCircle"
          alt=""
          aria-hidden="true"
          loading="lazy"
        />
      </div>
      <div :class="[$style.backgroundImage, { [$style.is24]: isIs24 }]"></div>
      <div :class="[$style.attributesImage, { [$style.is24]: isIs24 }]">
        <img
          :src="smallAttributesImage"
          :class="$style.small"
          :alt="$t('landing.img.attributes.alt')"
        />
        <img
          :src="bigAttributesImage"
          :class="$style.big"
          :alt="$t('landing.img.attributes.alt')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import gradientCircleUrl from '@/assets/images/effects/gradient-circle.webp';
import attributesBigDeHg from '@/assets/images/landing-teaser-attributes/Attributes_Big_de_hg.svg?url';
import attributesBigDeIs24 from '@/assets/images/landing-teaser-attributes/Attributes_Big_de_is24.svg?url';
import attributesBigEnHg from '@/assets/images/landing-teaser-attributes/Attributes_Big_en_hg.svg?url';
import attributesBigEnIs24 from '@/assets/images/landing-teaser-attributes/Attributes_Big_en_is24.svg?url';
import attributesBigFrHg from '@/assets/images/landing-teaser-attributes/Attributes_Big_fr_hg.svg?url';
import attributesBigFrIs24 from '@/assets/images/landing-teaser-attributes/Attributes_Big_fr_is24.svg?url';
import attributesBigItHg from '@/assets/images/landing-teaser-attributes/Attributes_Big_it_hg.svg?url';
import attributesBigItIs24 from '@/assets/images/landing-teaser-attributes/Attributes_Big_it_is24.svg?url';
import attributesSmallDeHg from '@/assets/images/landing-teaser-attributes/Attributes_Small_de_hg.svg?url';
import attributesSmallDeIs24 from '@/assets/images/landing-teaser-attributes/Attributes_Small_de_is24.svg?url';
import attributesSmallEnHg from '@/assets/images/landing-teaser-attributes/Attributes_Small_en_hg.svg?url';
import attributesSmallEnIs24 from '@/assets/images/landing-teaser-attributes/Attributes_Small_en_is24.svg?url';
import attributesSmallFrHg from '@/assets/images/landing-teaser-attributes/Attributes_Small_fr_hg.svg?url';
import attributesSmallFrIs24 from '@/assets/images/landing-teaser-attributes/Attributes_Small_fr_is24.svg?url';
import attributesSmallItHg from '@/assets/images/landing-teaser-attributes/Attributes_Small_it_hg.svg?url';
import attributesSmallItIs24 from '@/assets/images/landing-teaser-attributes/Attributes_Small_it_is24.svg?url';
import { getCurrentPlatform, isPlatformIs24 } from '@/helpers/Platform/Platform';

const { locale } = useI18n();

const platform = getCurrentPlatform();
const isIs24 = isPlatformIs24();

const smallAttributeImages: Record<string, string> = {
  de_hg: attributesSmallDeHg,
  de_is24: attributesSmallDeIs24,
  en_hg: attributesSmallEnHg,
  en_is24: attributesSmallEnIs24,
  fr_hg: attributesSmallFrHg,
  fr_is24: attributesSmallFrIs24,
  it_hg: attributesSmallItHg,
  it_is24: attributesSmallItIs24,
};

const bigAttributeImages: Record<string, string> = {
  de_hg: attributesBigDeHg,
  de_is24: attributesBigDeIs24,
  en_hg: attributesBigEnHg,
  en_is24: attributesBigEnIs24,
  fr_hg: attributesBigFrHg,
  fr_is24: attributesBigFrIs24,
  it_hg: attributesBigItHg,
  it_is24: attributesBigItIs24,
};

const imageKey = computed(() => `${locale.value}_${platform}`);

const smallAttributesImage = computed(
  () => smallAttributeImages[imageKey.value] ?? smallAttributeImages[`de_${platform}`],
);
const bigAttributesImage = computed(
  () => bigAttributeImages[imageKey.value] ?? bigAttributeImages[`de_${platform}`],
);
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.teaser {
  display: flex;
  flex-direction: column;
  align-items: center;

  @include grid-media-query(sm) {
    flex-direction: row;

    &.is24 {
      flex-direction: row-reverse;
    }
  }

  @include grid-media-query(md) {
    justify-content: center;
  }

  .content {
    h2 {
      margin-bottom: $space-50;

      @include heading-2;
    }

    p {
      margin-top: 0;

      @include body-2;
    }

    @include grid-media-query(sm) {
      margin-right: $space-400;

      &.is24 {
        margin-right: 0;
        margin-left: $space-400;
      }
    }

    @include grid-media-query(md) {
      width: 408px;
    }
  }

  .images {
    position: relative;
    width: 100%;
    margin-top: $space-150;
    margin-bottom: $space-250;

    @include grid-media-query(sm) {
      width: unset;
    }

    .gradientCircleContainer {
      position: absolute;
      inset: 0;
      overflow-x: clip;

      .gradientCircle {
        position: absolute;
        top: -$space-500;
        left: 35%;
        right: auto;
        z-index: -1;
        width: calc($space-1000 * 2 + $space-500);
        height: calc($space-1000 * 2 + $space-500);
        pointer-events: none;
        object-fit: contain;
        transform: translateX(-33%) rotate(60deg) scale(0.9);

        @include grid-media-query(sm) {
          left: 30%;
        }

        @include grid-media-query(md) {
          left: 40%;
        }
      }
    }

    .backgroundImage {
      width: calc(100% - $space-400);
      height: 281px;
      margin-right: $space-400;
      background: url('@/assets/images/landing-teaser-image-hg.jpg') no-repeat 60% center;
      background-size: cover;
      border-radius: $border-radius-l;

      &.is24 {
        background-image: url('@/assets/images/landing-teaser-image-is24.jpg');
      }

      @include grid-media-query(sm) {
        width: 272px;
        margin-right: $space-400;
      }

      @include grid-media-query(md) {
        width: 360px;
        height: 371px;
        margin-right: $space-800;
      }
    }

    .attributesImage {
      position: absolute;
      top: 200px;
      right: 0;
      left: unset;
      width: 307px;
      height: 120px;

      @include grid-media-query(md) {
        top: 227px;
        left: 52px;
        width: 412px;
        height: 184px;
      }

      img {
        width: 307px;
        height: 120px;
        border-radius: $border-radius-l;
        box-shadow: $elevation-medium;

        @include grid-media-query(md) {
          width: 412px;
          height: 184px;
        }

        &.small {
          display: block;

          @include grid-media-query(md) {
            display: none;
          }
        }

        &.big {
          display: none;

          @include grid-media-query(md) {
            display: block;
          }
        }
      }
    }
  }
}
</style>
